import { useEffect, useState } from "react";
import axios from "axios";
import { api } from "@/lib/api";

// Fallback statico per evitare schermate vuote prima della risposta API o in caso di errore di rete.
// Mantenere allineato a DEFAULT_POLICY_TYPES in backend/server.py.
const FALLBACK = [
  { code: "auto", label: "Auto", color: "blue", sort_order: 10 },
  { code: "moto", label: "Moto", color: "violet", sort_order: 15 },
  { code: "casa", label: "Casa", color: "emerald", sort_order: 20 },
  { code: "vita", label: "Vita", color: "rose", sort_order: 30 },
  { code: "salute", label: "Salute", color: "teal", sort_order: 40 },
  { code: "infortuni", label: "Infortuni", color: "orange", sort_order: 50 },
  { code: "viaggio", label: "Viaggio", color: "sky", sort_order: 60 },
  { code: "responsabilita_civile", label: "Responsabilità civile", color: "indigo", sort_order: 70 },
];

// Cache in-memory per evitare fetch ripetuti durante la stessa sessione.
let _cache = null;
let _publicCache = null;
let _inflight = null;
let _inflightPublic = null;

/**
 * Hook per ottenere l'elenco dinamico delle tipologie polizza configurate in /api/policy-types.
 * - `publicMode=true` usa l'endpoint pubblico (senza autenticazione) per il form pubblico/area cliente non-loggata.
 * - Restituisce sempre un array `[{v: code, l: label, color, ...}]` per compatibilità con i Select esistenti.
 */
export function usePolicyTypes(publicMode = false) {
  const initial = publicMode ? _publicCache : _cache;
  const [types, setTypes] = useState(initial || FALLBACK);
  const [loaded, setLoaded] = useState(!!initial);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        if (publicMode) {
          if (_publicCache) {
            if (!cancelled) { setTypes(_publicCache); setLoaded(true); }
            return;
          }
          if (!_inflightPublic) {
            const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
            _inflightPublic = axios.get(`${apiBase}/public/policy-types`).then((r) => r.data).catch(() => null);
          }
          const data = await _inflightPublic;
          _inflightPublic = null;
          if (Array.isArray(data) && data.length) _publicCache = data;
          if (!cancelled && _publicCache) { setTypes(_publicCache); setLoaded(true); }
        } else {
          if (_cache) {
            if (!cancelled) { setTypes(_cache); setLoaded(true); }
            return;
          }
          if (!_inflight) {
            _inflight = api.get("/policy-types").then((r) => r.data).catch(() => null);
          }
          const data = await _inflight;
          _inflight = null;
          if (Array.isArray(data) && data.length) _cache = data;
          if (!cancelled && _cache) { setTypes(_cache); setLoaded(true); }
        }
      } catch {
        /* keep fallback */
      }
    };
    fetchOnce();
    return () => { cancelled = true; };
  }, [publicMode]);

  // Adatta al formato {v, l} usato dai Select esistenti senza perdere campi originali.
  const options = (types || []).map((t) => ({ v: t.code, l: t.label, color: t.color, icon: t.icon }));
  const labelByCode = Object.fromEntries(options.map((o) => [o.v, o.l]));
  const findLabel = (code) => labelByCode[code] || code || "—";
  return { types, options, labelByCode, findLabel, loaded };
}

// Util per invalidare la cache (ad es. dopo create/update/delete in PolicyTypes.jsx).
export function invalidatePolicyTypesCache() {
  _cache = null;
  _publicCache = null;
  _inflight = null;
  _inflightPublic = null;
}
